import useEventListener from '../../hooks/useEventListener.hooks';

/**
 * HITL-1499: `Alt+I` files an issue report. Cloned deliberately from
 * `useIntentHotkeys` — same modifier guard, same `event.code` match, same
 * window-capture listener — because that shape buys three properties, none of
 * them optional:
 *
 * 1. **The interception dialog never sees the keydown.** AgentInterceptionConfirm
 *    listens on `document` in the capture phase; window capture is strictly
 *    outside document capture, so `stopPropagation()` here stops it. This is the
 *    mechanism, not a hope — it is how `Alt+1…9` avoids the dialog today.
 * 2. **It coexists with the other window-capture listeners.** `useIntentHotkeys`,
 *    PulseAdvise (`Alt+A`/`Alt+D`), KeyboardShortcutsHelp (`Alt+/`) and
 *    AutocompleteComp's Alt tracker are all siblings on `window`.
 *    `stopPropagation` does not stop siblings on the same target, so ordering
 *    between them never matters and the disjoint `e.code` checks keep them from
 *    overlapping. That sibling property is also what keeps AutocompleteComp's
 *    tracker observing our Alt.
 * 3. **macOS `Option+i` cannot leak a glyph.** `Option+i` is a dead key
 *    (circumflex); `preventDefault()` on keydown does not stop the text-input
 *    service from committing it, but `onTextChange` refuses any change while the
 *    Alt tracker is armed.
 *
 * `Alt+I` rather than a bare `F` (the source doc's suggestion): the utterance
 * input is permanently focused, so a bare letter is unusable. `Alt+1…9`, `Alt+A`,
 * `Alt+D` and `Alt+/` are already taken.
 *
 * `event.code` is matched so it stays layout-independent and unaffected by Alt
 * remapping the produced character.
 */
const useFlagIssueHotkey = (onFlag: () => void) => {
  const handleKeyDown = (event: Event) => {
    const e = event as KeyboardEvent;
    if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return;
    }
    if (e.code !== 'KeyI') {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onFlag();
  };

  useEventListener('keydown', handleKeyDown, window, true);
};

export default useFlagIssueHotkey;
